export const ACTIVE_PAGE_UUID_KEY = 'activePageUUID'
export const ACTIVE_PAGE_OWNER_KEY = 'activePageOwner'
export const ACTIVE_PAGE_REPLACED_REASON = 'active-page-replaced'
export const ACTIVE_PAGE_REGISTRATION_FAILED_REASON = 'active-page-registration-failed'
export const STALE_AUTH_RESPONSE_REASON = 'stale-auth-response'
export const ACTIVE_PAGE_REPLACED_EVENT = 'ACTIVE_PAGE_REPLACED'
export const TOKEN_CLEARED_EVENT = 'TOKEN_CLEARED'
export const AUTO_LOGIN_QUERY_KEY = '_autoLogin'
const ACTIVE_PAGE_REGISTRATION_LOCK = 'eoms-active-page-registration'
let activePageRegistrationCount = 0

export function normalizeActivePageUUID(activePageUUID) {
  if (typeof activePageUUID !== 'string') return null

  const value = activePageUUID.trim()
  if (!value || value === 'null' || value === 'undefined') return null
  return value
}

function getBrowserStorage(name) {
  try {
    if (typeof window === 'undefined') return null
    return window[name] || null
  } catch (error) {
    return null
  }
}

function readStorageValue(storage, key) {
  if (!storage) return null
  try {
    return normalizeActivePageUUID(storage.getItem(key))
  } catch (error) {
    return null
  }
}

function removeStorageValue(storage, key) {
  if (!storage) return
  try {
    storage.removeItem(key)
  } catch (error) {
    // 浏览器禁用存储时保留内存状态，不阻断登录流程。
  }
}

function getNavigationType(performanceRef) {
  const browserPerformance = performanceRef || (
    typeof performance === 'undefined' ? null : performance
  )
  if (!browserPerformance || typeof browserPerformance.getEntriesByType !== 'function') {
    return 'navigate'
  }

  try {
    const entries = browserPerformance.getEntriesByType('navigation')
    return entries && entries[0] && entries[0].type ? entries[0].type : 'navigate'
  } catch (error) {
    return 'navigate'
  }
}

export function prepareActivePageUUID(options = {}) {
  const pageStorage = options.sessionStorageRef || getBrowserStorage('sessionStorage')
  const sharedStorage = options.localStorageRef || getBrowserStorage('localStorage')
  const navigationType = options.navigationType || getNavigationType(options.performanceRef)

  removeStorageValue(sharedStorage, ACTIVE_PAGE_UUID_KEY)
  if (navigationType !== 'reload') {
    removeStorageValue(pageStorage, ACTIVE_PAGE_UUID_KEY)
    return null
  }

  return readStorageValue(pageStorage, ACTIVE_PAGE_UUID_KEY)
}

export function persistActivePageUUID(activePageUUID, pageStorageRef) {
  const pageStorage = pageStorageRef || getBrowserStorage('sessionStorage')
  const value = normalizeActivePageUUID(activePageUUID)
  if (!pageStorage) return value

  try {
    if (value) {
      pageStorage.setItem(ACTIVE_PAGE_UUID_KEY, value)
    } else {
      pageStorage.removeItem(ACTIVE_PAGE_UUID_KEY)
    }
  } catch (error) {
    // 浏览器禁用存储时保留内存状态，不阻断登录流程。
  }
  return value
}

export function publishActivePageOwner(activePageUUID, options = {}) {
  const sharedStorage = options.localStorageRef || getBrowserStorage('localStorage')
  const value = normalizeActivePageUUID(activePageUUID)
  if (!sharedStorage || !value) return null

  const now = typeof options.now === 'function' ? options.now : Date.now
  try {
    sharedStorage.setItem(ACTIVE_PAGE_OWNER_KEY, JSON.stringify({
      uuid: value,
      changedAt: now(),
    }))
    return value
  } catch (error) {
    return null
  }
}

export function getActivePageOwnerUUID(sharedStorageRef) {
  const sharedStorage = sharedStorageRef || getBrowserStorage('localStorage')
  if (!sharedStorage) return null

  try {
    const record = JSON.parse(sharedStorage.getItem(ACTIVE_PAGE_OWNER_KEY) || 'null')
    return record ? normalizeActivePageUUID(record.uuid) : null
  } catch (error) {
    return null
  }
}

export function isActivePageReplaced(activePageUUID, sharedStorageRef) {
  const pageUUID = normalizeActivePageUUID(activePageUUID)
  const ownerUUID = getActivePageOwnerUUID(sharedStorageRef)
  return !!(pageUUID && ownerUUID && pageUUID !== ownerUUID)
}

export function registerActivePage(options) {
  const uuid = normalizeActivePageUUID(options && options.uuid)
  const request = options && options.request
  const publish = options && options.publish ? options.publish : publishActivePageOwner

  if (!uuid || typeof request !== 'function') {
    return Promise.reject(new Error('active page registration requires a UUID and request'))
  }

  const register = () => Promise.resolve()
    .then(() => request(uuid))
    .then(() => {
      publish(uuid)
      return uuid
    })
  const locksRef = options.locksRef || getBrowserLocks()
  let operation

  activePageRegistrationCount += 1
  try {
    operation = locksRef && typeof locksRef.request === 'function'
      ? locksRef.request(ACTIVE_PAGE_REGISTRATION_LOCK, register)
      : register()
  } catch (error) {
    operation = Promise.reject(error)
  }

  return Promise.resolve(operation)
    .catch(error => {
      if (isSharedAuthPreservingError(error) || isConfirmedAuthInvalidError(error)) {
        return Promise.reject(error)
      }
      return Promise.reject(createSharedAuthPreservingError(
        ACTIVE_PAGE_REGISTRATION_FAILED_REASON,
        error,
      ))
    })
    .then(
      value => {
        activePageRegistrationCount -= 1
        return value
      },
      error => {
        activePageRegistrationCount -= 1
        return Promise.reject(error)
      },
    )
}

export function isActivePageRegistrationPending() {
  return activePageRegistrationCount > 0
}

function getBrowserLocks() {
  try {
    return typeof navigator === 'undefined' ? null : navigator.locks
  } catch (error) {
    return null
  }
}

export function isConfirmedAuthInvalidError(error) {
  if (!error) return false
  const code = Number(error.code)
  const returnCode = Number(error.returnCode)
  return returnCode === 430 || code === 11011 || code === 11012 || code === 404
}

export function shouldAbortHandledAuthFailure(error, tokenValue) {
  return isConfirmedAuthInvalidError(error) && !String(tokenValue || '')
}

export function createSharedAuthPreservingError(reason, originalError) {
  const error = new Error(
    originalError && originalError.message
      ? originalError.message
      : String(reason || 'shared auth preserving error')
  )
  error.preserveSharedAuthReason = reason
  error.originalError = originalError
  return error
}

export function isSharedAuthPreservingError(error) {
  return !!(error && error.preserveSharedAuthReason)
}

function getOwnerUUIDFromRecord(recordValue) {
  if (typeof recordValue !== 'string' || !recordValue) return null
  try {
    const record = JSON.parse(recordValue)
    return record ? normalizeActivePageUUID(record.uuid) : null
  } catch (error) {
    return null
  }
}

export function classifyActivePageStorageEvent(event, activePageUUID, options = {}) {
  if (!event) return null

  if (event.key === ACTIVE_PAGE_OWNER_KEY) {
    if (options.registrationPending) return null

    const pageUUID = normalizeActivePageUUID(activePageUUID)
    const currentOwnerUUID = options.sharedStorageRef
      ? getActivePageOwnerUUID(options.sharedStorageRef)
      : null
    const ownerUUID = currentOwnerUUID || getOwnerUUIDFromRecord(event.newValue)
    return pageUUID && ownerUUID && pageUUID !== ownerUUID
      ? ACTIVE_PAGE_REPLACED_EVENT
      : null
  }

  if (event.key === 'tokenValue' && event.oldValue && !event.newValue) {
    return TOKEN_CLEARED_EVENT
  }

  return null
}

export function buildActivePageReplacedRoute() {
  return buildPreservedSharedLoginRoute(ACTIVE_PAGE_REPLACED_REASON)
}

export function buildPreservedSharedLoginRoute(reason) {
  return {
    name: 'login',
    query: {reason},
  }
}

export function shouldPreserveSharedLogin(route) {
  return !!(
    route &&
    route.query &&
    (
      route.query.reason === ACTIVE_PAGE_REPLACED_REASON ||
      route.query.reason === ACTIVE_PAGE_REGISTRATION_FAILED_REASON
    )
  )
}

export function buildLoginEntryRedirect(route, fromRoute, tokenValue) {
  const loginBridgeData = route && route.query ? route.query.newObj : ''
  const isInitialNavigation = !(fromRoute && fromRoute.name)
  if (
    !isInitialNavigation ||
    shouldPreserveSharedLogin(route) ||
    String(loginBridgeData || '').length > 0
  ) {
    return null
  }
  return String(tokenValue || '')
    ? {
        name: 'home',
        query: {[AUTO_LOGIN_QUERY_KEY]: '1'},
        replace: true,
      }
    : null
}

export function buildInitialNavigationRedirect(route, fromRoute, storedRouteName) {
  const query = route && route.query ? route.query : {}
  if (query[AUTO_LOGIN_QUERY_KEY] === '1') {
    return {name: 'home', replace: true}
  }
  if (
    storedRouteName &&
    !(fromRoute && fromRoute.name) &&
    route &&
    route.name !== storedRouteName
  ) {
    return {name: storedRouteName}
  }
  return null
}

export function classifyReturnCode430(options = {}) {
  if (isAuthSnapshotStale(options)) return STALE_AUTH_RESPONSE_REASON

  return isActivePageReplaced(options.currentActivePageUUID, options.sharedStorageRef)
    ? ACTIVE_PAGE_REPLACED_REASON
    : null
}

export function isAuthSnapshotStale(options = {}) {
  const requestActivePageUUID = normalizeActivePageUUID(options.requestActivePageUUID)
  const currentActivePageUUID = normalizeActivePageUUID(options.currentActivePageUUID)
  if (requestActivePageUUID !== currentActivePageUUID) return true

  const requestTokenValue = String(options.requestTokenValue || '')
  const currentTokenValue = String(options.currentTokenValue || '')
  return requestTokenValue !== currentTokenValue
}

export function handleSharedAuthPreservingFailure(options = {}) {
  const error = options.error
  if (!isSharedAuthPreservingError(error)) return false

  if (
    error.preserveSharedAuthReason === STALE_AUTH_RESPONSE_REASON ||
    error.preserveSharedAuthReason === ACTIVE_PAGE_REPLACED_REASON
  ) {
    if (typeof options.abort === 'function') options.abort()
    return true
  }

  if (typeof options.redirect === 'function') {
    options.redirect(buildPreservedSharedLoginRoute(error.preserveSharedAuthReason))
  }
  return true
}

export function runLoginPageCleanup(options = {}) {
  let sharedLoginCleared = false
  let pageStateCleared = false

  if (!options.preserveSharedLogin && typeof options.clearSharedLogin === 'function') {
    options.clearSharedLogin()
    sharedLoginCleared = true
  }
  if (typeof options.clearPageState === 'function') {
    options.clearPageState()
    pageStateCleared = true
  }

  return {sharedLoginCleared, pageStateCleared}
}

export function installActivePageSessionSync(options = {}) {
  const windowRef = options.windowRef || (
    typeof window === 'undefined' ? null : window
  )
  const router = options.router
  const store = options.store
  let sharedStorageRef = options.sharedStorageRef || null

  if (!sharedStorageRef && windowRef) {
    try {
      sharedStorageRef = windowRef.localStorage || null
    } catch (error) {
      sharedStorageRef = null
    }
  }

  if (!windowRef || typeof windowRef.addEventListener !== 'function' || !router || !store) {
    return () => {}
  }

  const handleStorage = event => {
    const action = classifyActivePageStorageEvent(event, store.state.activePageUUID, {
      registrationPending: isActivePageRegistrationPending(),
      sharedStorageRef,
    })
    const currentRoute = router.currentRoute && router.currentRoute.value
      ? router.currentRoute.value
      : {}

    if (action === ACTIVE_PAGE_REPLACED_EVENT) {
      if (!shouldPreserveSharedLogin(currentRoute)) {
        router.push(buildActivePageReplacedRoute())
      }
      return
    }

    if (action === TOKEN_CLEARED_EVENT && currentRoute.name !== 'login') {
      router.push({name: 'login'})
    }
  }

  windowRef.addEventListener('storage', handleStorage)
  return () => windowRef.removeEventListener('storage', handleStorage)
}

export function buildDashboardUrl(options = {}) {
  const origin = String(options.origin || '').replace(/\/$/, '')
  let prefix = String(options.prefix || '')
  if (prefix && prefix.charAt(0) !== '/') prefix = `/${prefix}`
  prefix = prefix.replace(/\/$/, '')

  const shopId = encodeURIComponent(String(options.shopId || ''))
  const activePageUUID = encodeURIComponent(normalizeActivePageUUID(options.activePageUUID) || '')
  return `${origin}${prefix}/dashboardData/index.html?shopId=${shopId}&activePageUUID=${activePageUUID}`
}
